import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Car, User, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";
import PlateBadge from "@/components/PlateBadge";

/* Plate-first global search (Ctrl/Cmd+K). Car drives in → type plate → everything opens. */
export default function GlobalSearch() {
  const nav = useNavigate();
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setRes(null); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get("/search", { params: { q: q.trim() } }); setRes(data); setOpen(true); } catch { setRes(null); }
    }, 250);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };
  const total = res ? res.vehicles.length + res.customers.length + res.repairs.length : 0;
  return (
    <div className="relative hidden md:block w-[300px] lg:w-[380px]" data-testid="global-search">
      <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground pointer-events-none" />
      <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={t("searchPlatePlaceholder")} className="h-9 w-full rounded-full border border-border bg-background/70 pl-9 pr-14 text-sm font-mono uppercase placeholder:normal-case placeholder:font-sans focus:outline-none focus:border-primary" data-testid="global-search-input" />
      <kbd className="absolute right-3 top-2 text-[10px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5">⌘K</kbd>
      {open && res && (
        <div className="absolute mt-2 w-full rounded-xl border border-border bg-card shadow-xl overflow-hidden z-50" data-testid="global-search-results">
          {total === 0 && <div className="p-4 text-sm text-muted-foreground">{t("noResults")}</div>}
          {res.repairs.length > 0 && <Group label={t("jobCards")}>{res.repairs.map((r) => (
            <Row key={r.id} icon={ClipboardList} onClick={() => go(`/repairs?card=${r.id}`)} testId={`search-repair-${r.card_number}`}>
              <span className="font-mono text-xs text-muted-foreground">{r.card_number}</span> {r.car_make} {r.car_model} <span className="text-muted-foreground">· {r.customer_name}</span>
              <span className={`ml-auto text-[10px] px-1.5 rounded-full border st-${r.status}`}>{t("status_" + r.status)}</span>
            </Row>))}</Group>}
          {res.vehicles.length > 0 && <Group label={t("vehicles")}>{res.vehicles.map((v) => (
            <Row key={v.id} icon={Car} onClick={() => go(`/vehicles?plate=${encodeURIComponent(v.plate)}`)} testId={`search-vehicle-${v.plate}`}>
              <PlateBadge plate={v.plate} size="sm" /> {v.make} {v.model} <span className="text-muted-foreground">· {v.customer_name}</span>
            </Row>))}</Group>}
          {res.customers.length > 0 && <Group label={t("customers")}>{res.customers.map((c) => (
            <Row key={c.id} icon={User} onClick={() => go(`/customers?id=${c.id}`)} testId={`search-customer-${c.id}`}>{c.name}{c.company_name ? ` · ${c.company_name}` : ""} <span className="text-muted-foreground">{c.phone}</span></Row>))}</Group>}
        </div>
      )}
    </div>
  );
}

const Group = ({ label, children }) => (
  <div><div className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>{children}</div>
);
const Row = ({ icon: Icon, onClick, children, testId }) => (
  <button onMouseDown={onClick} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent" data-testid={testId}><Icon className="h-4 w-4 text-muted-foreground shrink-0" />{children}</button>
);
